<script setup>
// A compact segmented toggle for m/yd/ft, used everywhere a unit needs to
// be picked. Replaces a native <select> full of "Meter"/"Yard"/"Feet" —
// that text needs more width than a phone screen wants to give it, and a
// three-way toggle never needs to truncate since each label is 1-2 chars.
defineProps({
  modelValue: { type: String, required: true },
  fullWidth: { type: Boolean, default: false },
});

defineEmits(['update:modelValue']);

const UNITS = [
  { value: 'm',  label: 'M' },
  { value: 'yd', label: 'Yd' },
  { value: 'ft', label: 'Ft' },
];
</script>

<template>
  <div class="unit-select" :class="{ 'full-width': fullWidth }" role="group" aria-label="Unit">
    <button
      v-for="u in UNITS"
      :key="u.value"
      type="button"
      class="unit-option"
      :class="{ active: modelValue === u.value }"
      :aria-pressed="modelValue === u.value"
      @click="$emit('update:modelValue', u.value)"
    >
      {{ u.label }}
    </button>
  </div>
</template>

<style scoped>

.unit-select {
  display: inline-flex;
  border: var(--border-width) solid var(--input-border);
  border-radius: var(--border-radius);
  overflow: hidden;
  flex-shrink: 0;
}

.unit-select.full-width {
  display: flex;
  width: 100%;
}

.unit-select.full-width .unit-option {
  flex: 1;
}

.unit-option {
  padding: var(--space-xs) var(--space-sm);
  border: none;
  background: var(--input-bg);
  color: var(--input-text);
  cursor: pointer;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
}

.unit-option + .unit-option {
  border-left: var(--border-width) solid var(--input-border);
}

.unit-option:hover:not(.active) {
  background: var(--color-bg-alt);
}

.unit-option.active {
  /* --color-primary sits too close to --input-bg in the dark theme to read
     as "selected" next to two unselected buttons in the same low-contrast
     range — accent is already the app's "this is highlighted" color
     (Add Row, Copy Table) and stays legible against every theme's
     background, dark included. */
  background: var(--color-accent);
  color: var(--color-text-light);
}

</style>
